import { GenerationResult, PromaxCustomerData } from '@/pages/admin/types/CustomerDataTemplate'
import { apiClient } from '@/services/api'

export interface DataGenerationRequest {
  templates: string[]
  count: number
  dealerId: number
  includeConversations: boolean
  includeAppointments: boolean
  randomizeData: boolean
  customPrefix: string
}

export interface DataGenerationResponse {
  success: boolean
  results: GenerationResult[]
  errors?: string[]
}

export interface EmailBurstRequest {
  count: number
  intervalMs: number
  dealerId?: string
}

export interface EmailBurstLeadResult {
  index: number
  success: boolean
  customerName: string
  customerEmail: string
  messageId?: string
  error?: string
}

export interface EmailBurstResponse {
  burstId: string
  requested: number
  sent: number
  failed: number
  intervalMs: number
  durationMs: number
  results: EmailBurstLeadResult[]
}

export const dataGeneratorApi = {

  /**
   * Get a full random lead payload from the lead-generator (same code path as cron).
   * Returns the entire customer payload so the dashboard can POST it to /customers/event
   * without duplicating name/phone/email/vehicle logic.
   */
  /**
   * Promax Volume Lead Simulator — ADF email burst (Promax → WebSocket → queue), not direct /customers/event.
   */
  async runPromaxVolumeEmailBurst(request: EmailBurstRequest): Promise<EmailBurstResponse> {
    return apiClient.post<EmailBurstResponse>('/lead-generator/email-burst', request)
  },

  async getRandomLeadPayload(dealerId: number): Promise<{ customer: PromaxCustomerData }> {
    const response = await apiClient.get<{ customer: PromaxCustomerData }>(
      `/lead-generator/random-lead?dealerId=${dealerId}`,
    )
    return response
  },

  /**
   * Generate a single customer event (for testing)
   * This creates a ProMax-formatted customer event and processes it
   *
   * Backend endpoint: POST /customers/event
   * This calls customerEventProcessorService.processEvent(event)
   */
  async generateSingleCustomer(
    customerData: PromaxCustomerData,
    dealerId: number
  ): Promise<{ success: boolean; customerId?: string; conversationId?: string; error?: string }> {
    try {
      // Create the ProMax event structure that matches the integration test
      const event = {
        customer: customerData,
        dealer_id: dealerId,
        customer_id: customerData.customer_id,
        timestamp: new Date().toISOString(),
      }

      // Post to customer event endpoint that processes ProMax events
      const response = await apiClient.post<{ success: boolean; customerId?: string; conversationId?: string; error?: string }>(
        '/customers/event',
        event
      )
      return response
    } catch (error) {
      console.error('Failed to generate customer:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate customer'
      }
    }
  },


  /**
   * Clean up generated test data
   * Backend endpoint: DELETE /customers/test-data
   * Deletes all test customers with externalId starting with the test prefix
   */
  async cleanupTestData(): Promise<{ 
    success: boolean; 
    deletedCount: number; 
    details?: {
      customers: number;
      conversations: number;
      messages: number;
      twilioConversations: number;
    }
  }> {
    try {
      const response = await apiClient.delete<{ 
        success: boolean; 
        deletedCount: number; 
        details?: {
          customers: number;
          conversations: number;
          messages: number;
          twilioConversations: number;
        }
      }>(
        `/customers/test-data`
      )
      return response
    } catch (error) {
      console.error('Failed to cleanup test data:', error)
      throw new Error('Failed to cleanup test data')
    }
  }
}
